import { QUERY_KEYS } from "@/libraries/constant";
import { applyDataChanges } from "@/libraries/transform";
import { MESSAGE_TAG } from "@/schemas";
import { hemiService } from "@/services";
import type {
	GetMessageDiscussionsResponse,
	MemberProfileResponse,
	MessageDiscussion,
} from "@/types";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { generateUUID } from "pubnub";

export const hemiMutations = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CREATE_HEMI_MESSAGE], {
		mutationFn: (payload: Record<"uid" | "text" | "channel_name", string>) =>
			hemiService.send(payload),
		retry: 2,
		onMutate: async (newMessage) => {
			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			if (!currentUser) return;
			await queryClient.cancelQueries({
				queryKey: [
					QUERY_KEYS.GET_MESSAGES,
					{ channel_name: newMessage.channel_name, tags: MESSAGE_TAG.AI },
				],
			});
			const currentDt = dayjs().unix();
			const optimizeMessageId = newMessage.uid ?? generateUUID();
			const optimizeMessage: MessageDiscussion = {
				id: optimizeMessageId,
				channel_name: newMessage.channel_name,
				user: currentUser.user,
				tags: MESSAGE_TAG.AI,
				system_message: false,
				id_db: -1,
				created_at: currentDt,
				updated_at: currentDt,
				reads: [currentUser.user.id],
				text: newMessage.text || null,
				documents: [],
				document_count: 0,
				document_size: "0.0 Kb",
				mentions: [],
				replies: [],
				preview: null,
				sent: false,
				strike: false,
				is_deleted: false,
				delivered: false,
				reply_to: null,
				invitation_status: null,
				latest_reply_date: null,
				reply_count: 0,
				mention_name: [],
				parent: null,
				is_active_user: false,
				accept_at: null,
				decline_at: null,
				unread_thread_messages: 0,
				_flag: "sent",
				_isMe: true,
			};
			queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
				[
					QUERY_KEYS.GET_MESSAGES,
					{ channel_name: newMessage.channel_name, tags: MESSAGE_TAG.AI },
				],
				(oldData) => {
					return {
						...oldData,
						pages:
							oldData?.pages?.map((p) => ({
								...p,
								messages: applyDataChanges(p.messages, [optimizeMessage], []),
							})) || [],
						pageParams: oldData?.pageParams ?? [1],
					};
				},
			);
			return { optimizeMessage };
		},
		onSuccess: (data, newMessage) => {
			queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
				[
					QUERY_KEYS.GET_MESSAGES,
					{ channel_name: newMessage.channel_name, tags: MESSAGE_TAG.AI },
				],
				(oldData) => {
					if (!oldData) return oldData;
					const firstPage = oldData.pages[0];
					const updatedFirstPage = {
						...firstPage,
						messages: [...firstPage.messages, data.message],
					};
					const updatedPages = [
						updatedFirstPage,
						...(oldData.pages.slice(1) ?? []),
					];
					return {
						...oldData,
						pages: updatedPages,
						pageParams: oldData.pageParams ?? [1],
					};
				},
			);
		},
	});
};
